// Route: '/game/result/:sessionid'
import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { MyInfoCircle } from '../PlayResultPage/PlayResultPage.styles';
import Nav from '../../components/Nav/Nav';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import {
  Chart,
  BarSeries,
  Title,
  ArgumentAxis,
  ValueAxis,
} from '@devexpress/dx-react-chart-material-ui';
import { Animation } from '@devexpress/dx-react-chart';
import { StoreContext } from '../../utils/store';
import { CharContainer, PageTitle, MainContainer } from './ResultPage.styles';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';

const ResultPage = () => {
  const { sessionid } = useParams();
  const context = useContext(StoreContext);
  const history = useHistory();
  const [api] = context.api;
  const [, setModalStatus] = context.modalStatus;

  // results state
  const [results, setResults] = useState([]);
  const [scores, setScores] = useState([]);
  const [points, setPoints] = useState([]);
  const [averageTimeTakenByQuestion, setAverageTimeTakenByQuestion] = useState([]);
  const [percentOfPeopleCorrectByQuestion, setPercentOfPeopleCorrectByQuestion] = useState([]);

  // material ui styles
  const useStyles = makeStyles(() => ({
    tableContainer: {
      margin: 20,
      minWidth: 350,
      maxWidth: 650,
    },
    table: {
      minWidth: 350,
      maxWidth: 650,
      width: '90%',
      margin: '0 auto',
      '& th': {
        fontSize: '1.2em',
        fontWeight: 'bold',
      },
      '& td': {
        fontSize: '1em',
      },
    },
    chart: {
      margin: '20px',
    }
  }));
  const InfoTooltip = withStyles((theme) => ({
    tooltip: {
      backgroundColor: '#f5f5f9',
      color: '#333333',
      maxWidth: 250,
      fontSize: theme.typography.pxToRem(14),
      border: '1px solid #dadde9',
    },
  }))(Tooltip);
  const classes = useStyles();
  // graph format
  const format = scale => scale.tickFormat(null, '%');

  // api call functions
  const getResults = async () => {
    const response = await api.makeAPIRequest(`admin/session/${sessionid}/results`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    try {
      const json = await response.json();
      if (response.status === 200) {
        return json.results;
      } else {
        setModalStatus(status => ({
          ...status,
          modalActive: true,
          overlayActive: true,
          modalHeaderText: 'Error',
          modalBodyText: json.error,
          modalColors: 'error',
        }));
      }
    } catch (e) {
      console.log('ERROR', e);
      setModalStatus(status => ({
        ...status,
        modalActive: true,
        overlayActive: true,
        modalHeaderText: 'Error',
        modalBodyText: 'Sorry! Can\' connect to the server.',
        modalColors: 'error',
      }));
    }
  };

  // results set up
  useEffect(async () => {
    const newResults = await getResults();
    setResults(newResults);
    const newScores = [];
    const newPoints = [];
    const newTotalTimeTaken = [];
    const newNumOfPeopleCorrectByQuestion = [];
    newResults.forEach((player, idx) => {
      let score = 0;
      let newAnsPoints = 0;
      console.log(player.name);
      console.log(player.answers);
      player.answers.forEach((ans, idx) => {
        const timeTaken = (new Date(ans.answeredAt) - new Date(ans.questionStartedAt)) / 1000;
        if (!newTotalTimeTaken[idx]) {
          newTotalTimeTaken[idx] = {
            question: idx + 1 + '',
            time: timeTaken
          };
        } else {
          newTotalTimeTaken[idx].time += timeTaken;
        }
        if (ans.correct) {
          score++;
          newAnsPoints += Math.round(ans.questionPoints * (1 - (timeTaken / (ans.questionDuration - 0.5))));
          if (!newNumOfPeopleCorrectByQuestion[idx]) {
            newNumOfPeopleCorrectByQuestion[idx] = {
              question: idx + 1 + '',
              numberOfPeopleCorrect: 1,
            };
          } else {
            newNumOfPeopleCorrectByQuestion[idx].numberOfPeopleCorrect++;
          }
        } else {
          if (!newNumOfPeopleCorrectByQuestion[idx]) {
            newNumOfPeopleCorrectByQuestion[idx] = {
              question: idx + 1 + '',
              numberOfPeopleCorrect: 0,
            };
          }
        }
      });
      newScores.push(score);
      newPoints.push(newAnsPoints);
    });
    const newAverageTimeTaken = newTotalTimeTaken.map(t => ({
      question: t.question,
      time: t.time / newResults.length
    }));
    setAverageTimeTakenByQuestion(newAverageTimeTaken);
    setScores(newScores);
    setPoints(newPoints);
    const newPercentOfPeopleCorrectByQuestion = newNumOfPeopleCorrectByQuestion.map(n => ({
      question: n.question,
      percentage: n.numberOfPeopleCorrect / newResults.length
    }));
    setPercentOfPeopleCorrectByQuestion(newPercentOfPeopleCorrectByQuestion);
  }, []);

  return (
    <section>
      <Nav
        visibilities={['false', 'false', 'false', 'true', 'false']}
        exit={() => history.push('/')}
      />
      <MainContainer >
        <PageTitle>Game results</PageTitle>
        <p>session {sessionid}</p>
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table className={classes.table} aria-label="performance-table">
            <caption>Player Performance</caption>
            <TableHead>
              <TableRow>
                <TableCell align='center'>Player</TableCell>
                <TableCell align='center'>Score</TableCell>
                <TableCell align='center'>
                  Points
                  <InfoTooltip title={
                                  <>
                                    <Typography color='inherit'>Points scored are the product of the percentage of time left and total points of the question.</Typography>
                                    <em><b>{'Equation: '}</b>{'points = (1 - (time taken/duration)) * total points'}</em><br/>
                                    <span>{'Total points scored is the sum of points scored of all questions'}</span>
                                  </>
                                }>
                    <span>
                      <MyInfoCircle />
                    </span>
                  </InfoTooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {points &&
                <>
                {points.sort((a, b) => b - a).slice(0, 5).map((p, idx) => (
                  <TableRow key={results[idx].name}>
                    <TableCell align='center'>
                      {results[idx].name}
                    </TableCell>
                    <TableCell align='center'>{`${scores[idx]} out of ${results[idx].answers.length}`}</TableCell>
                    <TableCell align='center'>{`${p}/${results[idx].answers.reduce((accumulator, currentValue) => accumulator + currentValue.questionPoints, 0)}`}</TableCell>
                  </TableRow>
                ))}
                </>
              }
            </TableBody>
          </Table>
        </TableContainer>
        <CharContainer>
          <Paper className={classes.chart}>
            <Chart
              data={percentOfPeopleCorrectByQuestion}
              label='Percentage-of-People-Correct-by-Question'
            >
              <ArgumentAxis />
              <ValueAxis tickFormat={format} />

              <BarSeries
                valueField="percentage"
                argumentField="question"
              />
              <Title text="Percentage of People Correct by Question" />
              <Animation />
            </Chart>
          </Paper>
          <Paper className={classes.chart}>
            <Chart
              data={averageTimeTakenByQuestion}
              label='Average-Time-Taken-by-Question'
            >
              <ArgumentAxis />
              <ValueAxis />
              <BarSeries
                valueField="time"
                argumentField="question"
              />
              <Title text="Average Time Taken by Question" />
              <Animation />
            </Chart>
          </Paper>
        </CharContainer>
      </MainContainer>
    </section>
  );
};

export default ResultPage;
